import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';

import {DashboardTileId} from '../types/DashboardTileId';
import {DashboardTilePlacement} from '../types/DashboardTilePlacement';
import {
  allocateDashboardGrid,
  dashboardGridTilesOverlap,
  isDashboardGridPositionValid,
} from './allocateDashboardGrid';
import {applyDashboardGridLayout} from './applyDashboardGridLayout';
import {closeEmptyDashboardRows} from './closeEmptyDashboardRows';
import {reconcileDashboardGrid} from './reconcileDashboardGrid';

export function showDashboardTile(
  placements: DashboardTilePlacement[],
  id: DashboardTileId,
  visibleIds: DashboardTileId[]
): DashboardTilePlacement[] {
  return match(placements.find((tile) => tile.id === id))
    .with(Pattern.nullish, () => placements)
    .with({isVisible: true}, () => placements)
    .otherwise((tile) => {
      const incumbents = reconcileDashboardGrid(
        placements.filter((placement) => placement.id !== id && visibleIds.includes(placement.id))
      );
      const stored =
        isNil(tile.column) || isNil(tile.row)
          ? null
          : {id, size: tile.size, column: tile.column, row: tile.row};
      const incoming =
        !isNil(stored) &&
        isDashboardGridPositionValid(stored) &&
        !incumbents.some((incumbent) => dashboardGridTilesOverlap(stored, incumbent))
          ? [stored]
          : allocateDashboardGrid([tile], {occupied: incumbents});

      // Commit the displayed incumbent coordinates as well, without moving any of them. A stored
      // rectangle below a board that has shrunk since would leave blank rows above it, so they close.
      return isNil(incoming)
        ? placements
        : applyDashboardGridLayout(
            placements,
            closeEmptyDashboardRows([...incumbents, ...incoming])
          ).map((placement) => (placement.id === id ? {...placement, isVisible: true} : placement));
    });
}
